import { useEffect, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { QuestHUD } from "../../quests/quest-hud";
import { getInventoryLimit } from "../../config/constants";
import { useWorld } from "../../hooks/use-world";
import { useGeolocation } from "hooks";
import { useStateValue } from "providers";
import { postDungeonChat } from "actions/api";
import { getActiveSave } from "../character-select";
import {
  Panel,
  Label,
  SideOverlay,
  OverlayHeader,
  CloseBtn,
  ItemList,
  ItemRow,
  ItemDot,
  ItemInfo,
  ItemName,
  ItemMeta,
  ActionBtn,
  EmptyMsg,
  SectionLabel,
  EquippedName,
} from "../../ui";
import {
  HUDContainer,
  HealthBar,
  ToggleButton,
  PriceTag,
  StatusToast,
  ChatContainer,
  ChatPanel,
  ChatMessages,
  ChatMsg,
  ChatAuthor,
  ChatInputRow,
  ChatInput,
  ChatSend,
} from "./styles";

export const HUD = ({ dungeonKey }) => {
  const {
    health,
    maxHealth,
    score,
    xp,
    wallet,
    inventory,
    currentLevel,
    removeFromInventory,
    healPlayer,
    shopInventory,
    closeShop,
    buyItem,
    dropItem,
    equipWeapon,
    equippedWeaponId,
    equipArmor,
    equippedArmorId,
    holdItem,
    heldItemId,
    autoDefence,
    toggleAutoDefence,
    chatMessages,
    statusMsg,
    respawnEntities,
    showInventory,
    setShowInventory,
  } = useWorld();
  const [hudCollapsed, setHudCollapsed] = useState(false);
  const [{ session }] = useStateValue();
  const { data: geoData } = useGeolocation();
  const { colors } = useTheme();
  const [showChat, setShowChat] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const messagesEndRef = useRef(null);
  const inventoryRef = useRef(null);

  const healthPct = Math.max(0, Math.min(100, (health / maxHealth) * 100));
  const lat = geoData?.latitude?.toFixed(4) ?? "—";
  const lng = geoData?.longitude?.toFixed(4) ?? "—";

  const TYPE_COLORS = {
    gold: colors("highlight"),
    weapon: colors("accent"),
    armor: colors("highlight", 0.7),
    potion: colors("link"),
  };

  const itemColor = (item) =>
    TYPE_COLORS[item.type] ?? colors("foreground", 0.5);

  useEffect(() => {
    const handler = (e) => {
      if (e.code === "KeyI" && e.target === document.body)
        setShowInventory((v) => !v);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!showInventory) return;
    const handler = (e) => {
      if (inventoryRef.current && !inventoryRef.current.contains(e.target))
        setShowInventory(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showInventory]);

  useEffect(() => {
    if (showChat) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, showChat]);

  const sendChat = () => {
    const msg = chatInput.trim();
    if (!msg || !dungeonKey) return;
    const characterName = getActiveSave()?.character?.name || null;
    postDungeonChat(dungeonKey, msg, characterName).catch(() => {});
    setChatInput("");
  };

  return (
    <>
      <QuestHUD />
      {statusMsg && <StatusToast>{statusMsg}</StatusToast>}
      <HUDContainer>
        <Panel
          onClick={() => setHudCollapsed((v) => !v)}
          style={{ cursor: "pointer", pointerEvents: "auto", userSelect: "none" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div><Label>HP</Label>{health}/{maxHealth}</div>
            <span style={{ opacity: 0.4, fontSize: "0.55rem", marginLeft: "0.75rem" }}>
              {hudCollapsed ? "▸" : "▾"}
            </span>
          </div>
          <HealthBar $pct={healthPct} />
        </Panel>
        {!hudCollapsed && <>
          <Panel>
            <div>
              <Label>Level</Label>
              {currentLevel + 1}
            </div>
            <div>
              <Label>Score</Label>
              {score}
            </div>
            <div>
              <Label>XP</Label>
              {xp}
            </div>
            <div>
              <Label>Gold</Label>
              {wallet}g
            </div>
          </Panel>
          <ToggleButton
            $open={showInventory}
            onClick={(e) => { e.stopPropagation(); setShowInventory((v) => !v); }}
          >
            {(() => {
              const weapon = equippedWeaponId && inventory.find((i) => i.id === equippedWeaponId);
              const armor = equippedArmorId && inventory.find((i) => i.id === equippedArmorId);
              const held = heldItemId && inventory.find((i) => i.id === heldItemId);
              if (weapon || armor || held) {
                const row = { display: "flex", alignItems: "center", gap: "0.3em", width: "100%" };
                return (
                  <span style={{ display: "flex", flexDirection: "column", gap: "0.15rem", width: "100%" }}>
                    {weapon && <span style={row}><span>⚔</span><EquippedName>{weapon.name || "Weapon"}</EquippedName></span>}
                    {armor && <span style={row}><span>🛡</span><EquippedName>{armor.name || "Armor"}</EquippedName></span>}
                    {held && <span style={row}><span>✋</span><EquippedName>{held.name || held.type}</EquippedName></span>}
                    <span style={{ opacity: 0.5, fontSize: "0.55rem" }}>↵ inventory</span>
                  </span>
                );
              }
              return `Inventory [${inventory.length}]${showInventory ? "  ×" : "  ↵"}`;
            })()}
          </ToggleButton>
          <ToggleButton
            $open={autoDefence}
            onClick={toggleAutoDefence}
            title="Auto-defence: automatically attack adjacent monsters"
          >
            {autoDefence ? "🛡 Auto ●" : "🛡 Auto ○"}
          </ToggleButton>
          {session?.user?.can_respawn && (
            <ToggleButton onClick={respawnEntities} title="Respawn all monsters and items">
              ↺ Respawn
            </ToggleButton>
          )}
          <Panel>
            <div>
              <Label>Lat</Label>
              {lat}
            </div>
            <div>
              <Label>Lng</Label>
              {lng}
            </div>
          </Panel>
        </>}
      </HUDContainer>

      {shopInventory.length > 0 && (
        <SideOverlay $right>
          <OverlayHeader>
            Wares
            <CloseBtn onClick={closeShop}>×</CloseBtn>
          </OverlayHeader>
          <ItemList>
            {shopInventory.map((item) => {
              const canAfford = wallet >= (item.price || 0);
              return (
                <ItemRow key={item.id}>
                  <ItemDot $color={itemColor(item)} />
                  <ItemInfo>
                    <ItemName>{item.name || item.type}</ItemName>
                    <ItemMeta>
                      {item.type}
                      {item.value ? ` · ${item.value}` : ""}
                    </ItemMeta>
                  </ItemInfo>
                  {item.price > 0 && <PriceTag>{item.price}g</PriceTag>}
                  <ActionBtn
                    $use
                    disabled={!canAfford}
                    onClick={() => buyItem(item)}
                  >
                    Buy
                  </ActionBtn>
                </ItemRow>
              );
            })}
          </ItemList>
        </SideOverlay>
      )}

      <ChatContainer>
        {showChat && (
          <ChatPanel>
            <ChatMessages>
              {chatMessages.map((msg, i) => (
                <ChatMsg key={msg.id ?? i}>
                  <ChatAuthor>{msg.username}</ChatAuthor>
                  {msg.message}
                </ChatMsg>
              ))}
              <div ref={messagesEndRef} />
            </ChatMessages>
            <ChatInputRow>
              <ChatInput
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); sendChat(); }
                  e.stopPropagation();
                }}
                placeholder="message..."
                maxLength={200}
              />
              <ChatSend onClick={sendChat}>Send</ChatSend>
            </ChatInputRow>
          </ChatPanel>
        )}
        <ToggleButton $fullWidth $open={showChat} onClick={() => setShowChat((v) => !v)}>
          Chat {chatMessages.length > 0 && !showChat ? `[${chatMessages.length}]` : showChat ? "×" : ""}
        </ToggleButton>
      </ChatContainer>

      {showInventory && (
        <SideOverlay ref={inventoryRef}>
          <OverlayHeader>
            Inventory
            <CloseBtn onClick={() => setShowInventory(false)}>×</CloseBtn>
          </OverlayHeader>
          <ItemList>
            {inventory.length === 0 && <EmptyMsg>No items carried.</EmptyMsg>}
            {(() => {
              const limit = getInventoryLimit(xp);
              return Object.entries(
                inventory.reduce((byType, item) => {
                  const type = item.type || "misc";
                  if (!byType[type]) byType[type] = {};
                  const nameKey = item.name || type;
                  if (!byType[type][nameKey]) byType[type][nameKey] = { rep: item, ids: [], count: 0 };
                  byType[type][nameKey].ids.push(item.id);
                  byType[type][nameKey].count += 1;
                  return byType;
                }, {})
              ).map(([type, stacks]) => {
              const uniqueStacks = Object.keys(stacks).length;
              return (
              <div key={type}>
                <SectionLabel>{type} ({uniqueStacks}/{limit})</SectionLabel>
                {Object.values(stacks).map(({ rep, ids, count }) => {
                  const isEquippedWeapon = ids.includes(equippedWeaponId);
                  const isEquippedArmor = ids.includes(equippedArmorId);
                  const isHeld = ids.includes(heldItemId);
                  const actionId = isEquippedWeapon ? equippedWeaponId
                    : isEquippedArmor ? equippedArmorId
                    : isHeld ? heldItemId
                    : rep.id;
                  return (
                    <ItemRow key={`${rep.type}|${rep.name}`}>
                      <ItemDot $color={itemColor(rep)} />
                      <ItemInfo>
                        <ItemName style={{ display: "flex", alignItems: "center" }}>
                          <EquippedName>{rep.name || rep.type}</EquippedName>
                          {count > 1 && <span style={{ opacity: 0.55, marginLeft: "0.3em", flexShrink: 0 }}>×{count}</span>}
                        </ItemName>
                        {rep.value ? <ItemMeta>{rep.value}</ItemMeta> : null}
                      </ItemInfo>
                      {type === "weapon" && (
                        <ActionBtn
                          $use={!isEquippedWeapon}
                          onClick={() => equipWeapon(isEquippedWeapon ? equippedWeaponId : rep.id)}
                          title={isEquippedWeapon ? "Unequip" : "Equip"}
                        >
                          {isEquippedWeapon ? "-" : "+"}
                        </ActionBtn>
                      )}
                      {type === "armor" && (
                        <ActionBtn
                          $use={!isEquippedArmor}
                          onClick={() => equipArmor(isEquippedArmor ? equippedArmorId : rep.id)}
                          title={isEquippedArmor ? "Unequip" : "Equip"}
                        >
                          {isEquippedArmor ? "-" : "+"}
                        </ActionBtn>
                      )}
                      {type === "potion" && (
                        <ActionBtn
                          $use
                          onClick={() => {
                            healPlayer(rep.value || 20);
                            removeFromInventory(rep.id);
                          }}
                        >
                          +
                        </ActionBtn>
                      )}
                      <ActionBtn
                        $use={isHeld}
                        onClick={() => holdItem(isHeld ? heldItemId : rep.id)}
                        title={isHeld ? "Release" : "Hold to give to NPC"}
                      >
                        {isHeld ? "✋" : "Hold"}
                      </ActionBtn>
                      <ActionBtn onClick={() => dropItem(inventory.find((i) => i.id === actionId) ?? rep)}>Drop</ActionBtn>
                    </ItemRow>
                  );
                })}
              </div>
            ); });
            })()}
          </ItemList>
        </SideOverlay>
      )}
    </>
  );
};
